import {
  Alert,
  Box,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Label, Scrollbar } from '@/components';
import { TableRowLink } from '@/components/table';
import { bookingsManagementPath } from '@/features/bookings-management/routes/paths';
import { fDateTime } from '@/utils/formatTime';
import { Referral } from '../types';
import { getReferralProgress, getReferralSessionBreakdown } from '../utils';

const safeDateTime = (value?: string | null) => {
  if (!value) return '-';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return fDateTime(value);
};

const formatAmount = (value?: number | string | null) => {
  if (value === undefined || value === null || value === '') return '-';

  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? `£${numericValue}` : String(value);
};

const getSessionFilter = (dateValue?: string | null) => {
  if (!dateValue) return 'upcoming';

  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return 'upcoming';

  const today = new Date();
  const sessionDay = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate()
  ).getTime();
  const todayDay = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate()
  ).getTime();

  if (sessionDay === todayDay) return 'today';
  return sessionDay < todayDay ? 'previous' : 'upcoming';
};

const getSessionPath = (
  bookingId: number | string | null,
  sessionId: number | string,
  date?: string | null
) => {
  if (!bookingId) return null;

  return bookingsManagementPath('view', {
    bookingId: String(bookingId),
    bookingTabSid: 'booking-sessions',
    sessionFilter: `${getSessionFilter(date)}?sessionID=${sessionId}`,
  });
};

export default function ReferralSessionBreakdown({
  referral,
}: {
  referral: Referral;
}) {
  const sessions = getReferralSessionBreakdown(referral);
  const progress = getReferralProgress(referral);

  if (!sessions.length) {
    const message =
      progress.completed > 0
        ? 'Session-level breakdown is not returned by the referral API yet.'
        : 'No completed paid sessions returned for this referral yet.';

    return (
      <Alert severity="info">
        {message} The progress above remains the API-computed source of truth.
      </Alert>
    );
  }

  return (
    <Stack spacing={1.5}>
      <Typography variant="body2" color="text.secondary">
        Sessions shown here are returned by the referral API; the dashboard does
        not recalculate progress.
      </Typography>
      <Scrollbar>
        <TableContainer sx={{ minWidth: 760 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Session</TableCell>
                <TableCell>Booking</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Payment</TableCell>
                <TableCell align="right">Paid amount</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sessions.map((session) => {
                const sessionPath = getSessionPath(
                  session.bookingId,
                  session.id,
                  session.date
                );

                return (
                  <TableRow
                    key={`${session.bookingId ?? 'booking'}-${session.id}`}
                  >
                    <TableCell>
                      <Stack spacing={0.25} alignItems="flex-start">
                        {sessionPath ? (
                          <TableRowLink
                            linkText={`Session #${session.id}`}
                            linkTo={sessionPath}
                            target="_blank"
                            sx={{ px: 0, py: 0 }}
                          />
                        ) : (
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            Session #{session.id}
                          </Typography>
                        )}
                        {session.label && (
                          <Typography variant="caption" color="text.secondary">
                            {session.label}
                          </Typography>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>{session.bookingId ?? '-'}</TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2">
                          {safeDateTime(session.date)}
                        </Typography>
                        {session.time && (
                          <Typography variant="caption" color="text.secondary">
                            {session.time}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {session.status ? (
                        <Label
                          color="info"
                          sx={{ textTransform: 'capitalize' }}
                        >
                          {session.status}
                        </Label>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell>
                      {session.paymentStatus ? (
                        <Label
                          color="success"
                          sx={{ textTransform: 'capitalize' }}
                        >
                          {session.paymentStatus}
                        </Label>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {formatAmount(session.amountPaid)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Scrollbar>
    </Stack>
  );
}
